import { useEffect, useState } from 'react';
import { Avatar } from '@mui/material';
import { getPhotoUrlCandidates } from 'utils/resolvePhotoUrl';

// Drop-in replacement for MUI's <Avatar> when the image comes from a stored
// path (photoPath) rather than something already known to be a valid URL.
// Tries each candidate host from resolvePhotoUrl in order; if one fails to
// load, it automatically tries the next before giving up and letting MUI's
// built-in fallback (the `children` you pass, e.g. initials) take over.
//
// Every attempt/failure is logged to the console in development, so
// checking devtools > console (not just the Network tab) will show exactly
// which URLs were tried and which one worked, if any.
//
// Props:
//   photoPath  - the raw stored path (or full URL), e.g. "assets/images/users/111/users/user_....jpg"
//   overrideSrc - if set (e.g. a local blob: preview URL for a freshly picked file),
//                 used directly and candidate cycling is skipped entirely.
const PhotoAvatar = ({ photoPath, overrideSrc, alt, children, sx, ...rest }) => {
  const [candidates, setCandidates] = useState(() => getPhotoUrlCandidates(photoPath));
  const [index, setIndex] = useState(0);

  useEffect(() => {
    setCandidates(getPhotoUrlCandidates(photoPath));
    setIndex(0);
  }, [photoPath]);

  const src = overrideSrc || candidates[index];

  if (process.env.NODE_ENV !== 'production' && typeof window !== 'undefined' && !window.__photoAvatarLoggedOnce) {
    // one-time hint so this is discoverable even without reading the code
    window.__photoAvatarLoggedOnce = true;
    // eslint-disable-next-line no-console
    console.info('[PhotoAvatar] logging photo load attempts to the console - look for [PhotoAvatar] lines below.');
  }

  return (
    <Avatar
      alt={alt}
      src={src}
      sx={sx}
      imgProps={{
        onError: () => {
          if (overrideSrc) return; // local preview - nothing to fall back to
          if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.warn(
              `[PhotoAvatar] failed to load "${src}" (raw path: "${photoPath}")` +
              (index + 1 < candidates.length ? ' - trying next candidate...' : ' - no more candidates, showing fallback.')
            );
          }
          setIndex((i) => i + 1);
        },
        onLoad: () => {
          if (process.env.NODE_ENV !== 'production' && src) {
            // eslint-disable-next-line no-console
            console.info(`[PhotoAvatar] loaded successfully: "${src}"`);
          }
        },
      }}
      {...rest}
    >
      {children}
    </Avatar>
  );
};

export default PhotoAvatar;
